import axios from "axios";

/**
 * Data sources for the Customer Overview page. Each call mirrors what the mobile home screen
 * does (ahd-mobile/src/screens/Dashboard/*): same endpoints, payloads and location fallbacks.
 */
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

/** User's own city / state / areas (mobile HomeScreen: GET admin/user/id/{id}). */
export const getUserLocation = async (token, userId) => {
  if (!userId) return { state: "", city: "", areas: [] };
  const res = await axios.get(`${BASE_URL}/admin/user/id/${userId}`, { headers: getAuthHeaders(token) });
  const user = res.data?.user || {};
  const city = user.cities?.[0];
  return {
    state: city?.state?.name || "",
    city: city?.name || "",
    areas: (user.areas || []).map((a) => a.name),
  };
};

const MAX_ITEMS = 5;

/**
 * Reads the list out of a response whatever its shape (data.<key>, data.data, data.items,
 * data.results ...) and drops empty / id-less entries, so "no valid items" counts as empty.
 */
export const pickList = (payload, keys = []) => {
  const candidates = [...keys.map((k) => payload?.[k]), payload?.data, payload?.items, payload?.results];
  const found = candidates.find((c) => Array.isArray(c)) || [];
  return found.filter((item) => item && item.id != null);
};

/**
 * Mobile NearbyProperties: try the user's areas, then city, then state, then everything,
 * until five properties are collected.
 */
export const getNearbyProperties = async (token, location) => {
  const base = { type: "", purpose: "", price: 0, keyboard_search: "", page_number: 1, page_size: MAX_ITEMS, order_by: "DESC" };
  const payloads = [
    { ...base, state: "", city: "", area: location.areas?.length ? location.areas.join(",") : "" },
    { ...base, state: "", city: location.city, area: "" },
    { ...base, state: location.state, city: "", area: "" },
    { ...base, state: "", city: "", area: "" },
  ];

  let collected = [];
  let assetUrl = "";
  let lastError = null;
  let anySucceeded = false;

  for (const payload of payloads) {
    try {
      const res = await axios.post(`${BASE_URL}/property/v1/list/preferred`, payload, {
        headers: getAuthHeaders(token),
        timeout: 15000,
      });
      anySucceeded = true;
      const list = res.data?.statusType === "SUCCESS" ? pickList(res.data, ["properties"]) : [];
      if (list.length) {
        assetUrl = res.data.assetUrl || assetUrl;
        const seen = collected;
        const fresh = list.filter((p) => !seen.some((c) => c.id === p.id));
        collected = [...collected, ...fresh];
        if (collected.length >= MAX_ITEMS) break;
      }
    } catch (err) {
      lastError = err;
    }
  }

  if (!anySucceeded && lastError) throw lastError;
  return { properties: collected.slice(0, MAX_ITEMS), assetUrl };
};

/** Mobile NearbyPosts: user's city, then state, then everything. */
export const getNearbyPosts = async (token, location) => {
  const base = { page_number: 1, page_size: 6, order_by: "DESC", service: "" };
  const payloads = [
    { ...base, state: "", city: location.city },
    { ...base, state: location.state, city: "" },
    { ...base, state: "", city: "" },
  ];

  let collected = [];
  let lastError = null;
  let anySucceeded = false;

  for (const payload of payloads) {
    try {
      const res = await axios.post(`${BASE_URL}/post/list/preferred`, payload, { headers: getAuthHeaders(token) });
      anySucceeded = true;
      const list = res.data?.statusType === "SUCCESS" ? pickList(res.data, ["leads"]) : [];
      if (list.length) {
        const seen = collected;
        const fresh = list.filter((l) => !seen.some((c) => c.id === l.id));
        collected = [...collected, ...fresh];
        if (collected.length >= MAX_ITEMS) break;
      }
    } catch (err) {
      lastError = err;
    }
  }

  if (!anySucceeded && lastError) throw lastError;
  return collected.slice(0, MAX_ITEMS);
};

/** Mobile NearbyPromotions for customers: POST /promotion/ */
export const getOverviewPromotions = async (token) => {
  const res = await axios.post(
    `${BASE_URL}/promotion/`,
    { page_number: 1, page_size: 20, order_by: "DESC" },
    { headers: getAuthHeaders(token) }
  );
  if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Failed to load promotions");
  return pickList(res.data, ["promotions"]);
};

/** Mobile NearbyInterior / InteriorDesign: POST /interior/catalogs */
export const getInteriorCatalogs = async (token, { page_size = 6, keyword_search = "" } = {}) => {
  const res = await axios.post(
    `${BASE_URL}/interior/catalogs`,
    { page_number: 1, page_size, order_by: "DESC", keyword_search, type_id: "", min_price: 0, max_price: "" },
    { headers: getAuthHeaders(token) }
  );
  if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Failed to load interior designs");
  return pickList(res.data, ["catalogs"]);
};

/** GET /interior/catalog/details/{id} (mobile InteriorCatalogDetail) */
export const getInteriorCatalog = async (token, catalogId) => {
  const res = await axios.get(`${BASE_URL}/interior/catalog/details/${catalogId}`, { headers: getAuthHeaders(token) });
  if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Failed to load interior design");
  return res.data.catalog || res.data.interiorCatalog || res.data.catalogs || null;
};

/** Mobile ServiceCategories (Home Services): POST /api/all/services/types -> allServiceCategories */
export const getHomeServiceCategories = async (token) => {
  const res = await axios.post(
    `${BASE_URL}/api/all/services/types`,
    { page_number: 1, page_size: 50, keyword_search: "", order_by: "DESC" },
    { headers: getAuthHeaders(token) }
  );
  if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Failed to load services");
  return pickList(res.data, ["allServiceCategories"]);
};

/** Follow / unfollow a property (same calls as the property cards). */
export const followProperty = async (token, propertyId) => {
  const res = await axios.post(
    `${BASE_URL}/property/favorite/add`,
    { property_id: Number(propertyId) },
    { headers: getAuthHeaders(token) }
  );
  if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Unable to follow property");
  return res.data;
};

export const unfollowProperty = async (token, favoriteId) => {
  const res = await axios.delete(`${BASE_URL}/property/favorite/delete/${Number(favoriteId)}`, {
    headers: getAuthHeaders(token),
  });
  if (res.data?.statusType !== "SUCCESS") throw new Error(res.data?.text || "Unable to unfollow property");
  return res.data;
};
